import { useEffect } from "react";
import { Flipped, Flipper } from "react-flip-toolkit";
import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiButton,
  MiIcon,
  MiMenu,
  MiPlaceholderArea,
} from "@miplatform/components";
import { BUTTON_VARIANTS } from "@miplatform/constants";
import {
  IBidArea,
  ICategoryFilterResponse,
  IMaterialsResponse,
} from "@miplatform/types";
import { filterMaterialsByAttributes } from "@miplatform/utils";
import { mdiFloorPlan, mdiPlus } from "@mdi/js";
import { SearchResultsContainer } from "@mibuildweb/components";
import { Box, Typography } from "@mui/material";
import { AddAreaDrawer } from "./components/AddAreaDrawer/AddAreaDrawer";
import { BidArea } from "./components/BidArea/BidArea";
import { MaterialResultCard } from "./components/MaterialResultCard/MaterialResultCard";
import { CategoryFilterDrawer } from "../../../components/FilterDrawer/CategoryFilterDrawer";
import { useCreateBidContext } from "../CreateBidContext";
import { AREA_COLUMNS } from "../constants";
import { useConfigureBidViewModel } from "./useConfigureBidViewModel";

export function ConfigureBid() {
  const {
    floors,
    openAreaDrawer,
    selectedFloor,
    addedFloorIds,
    addedFloors,
    groupedAreas,
    planAreas,
    showCategoryFilter,
    selectedCategory,
    selectedAttributeValues,
    search,
    materialOptions,
    materialResults,
    openMaterialResults,
    selectedItem,
    isSuppliedByBuilder,
    checkedAreas,
    planAreaMaterials,
    hasMissingCategories,
    planAreaMaterialIds,
    planAreaMaterialIdsKey,
    debouncedSearch,
    categoryFilterOptions,
    setSearch,
    setOpenAreaDrawer,
    setSelectedFloor,
    setPreviewAreas,
    setOpenMaterialResults,
    setIsSuppliedByBuilder,
    setSelectedItem,
    setFilteredMaterialOptions,
    getAreaType,
    getFloors,
    getAreaTypes,
    getMaterialOptions,
    getPlanAreaMaterial,
    getCategoryFilters,
    isPreviewArea,
    handleAddFloor,
    handleRemoveFloor,
    handleOpenCategoryFilter,
    handleCloseCategoryFilter,
    handleCategoryClick,
    handleAttributeValueClick,
    handleCheckArea,
    handleCancelItem,
    handleSelectItem,
    handleAddItemToAreas,
    resetMaterialDrawerStates,
  } = useConfigureBidViewModel();

  const { classes, theme } = useStyles();
  const { setIsConfigStepValid } = useCreateBidContext();

  useEffect(() => {
    getFloors();
    getAreaTypes();
    getCategoryFilters();
  }, []);

  useEffect(() => {
    if (planAreaMaterialIds.length > 0) {
      getPlanAreaMaterial();
    }
  }, [planAreaMaterialIdsKey]);

  useEffect(() => {
    if (
      showCategoryFilter &&
      (debouncedSearch || selectedCategory?.categoryId)
    ) {
      getMaterialOptions();
    }
  }, [debouncedSearch, selectedCategory]);

  useEffect(() => {
    if (
      selectedCategory?.categoryId &&
      Object.keys(selectedAttributeValues).length > 0
    ) {
      setFilteredMaterialOptions(
        filterMaterialsByAttributes(materialOptions, selectedAttributeValues)
      );
    } else {
      setFilteredMaterialOptions(materialOptions);
    }
  }, [materialOptions, selectedAttributeValues]);

  useEffect(() => {
    setIsConfigStepValid(!hasMissingCategories);
  }, [hasMissingCategories]);

  const renderColumn = (areas: IBidArea[], column: number) => {
    const columnAreas = areas.filter((a) => a.specColumn === column);
    return (
      <Flipper
        flipKey={columnAreas.map((a) => a.id).join("-")}
        className={classes.columnBox}
      >
        {columnAreas.map((area, index) => {
          const isPreview = isPreviewArea(area);
          const areaType = getAreaType(area);

          return (
            <Flipped key={area.id} flipId={area.id}>
              {(flippedProps) => (
                <Box {...flippedProps}>
                  <BidArea
                    area={area}
                    areaType={areaType}
                    planAreaMaterials={planAreaMaterials}
                    isPreview={isPreview}
                    checkedAreas={checkedAreas}
                    selectedCategory={selectedCategory}
                    openMaterialResults={openMaterialResults}
                    disableUp={index === 0}
                    disableDown={index === columnAreas.length - 1}
                    showCategoryFilter={showCategoryFilter}
                    openAreaDrawer={openAreaDrawer}
                    handleCheckArea={handleCheckArea}
                    handleOpenItemDrawer={() => handleOpenCategoryFilter(area)}
                    handlePlaceholder={(cat) => {
                      const filterCategory = categoryFilterOptions.find(
                        (c: ICategoryFilterResponse) =>
                          c.categoryId === cat.categoryId
                      );
                      handleOpenCategoryFilter(area, filterCategory || cat);
                    }}
                  />
                </Box>
              )}
            </Flipped>
          );
        })}
      </Flipper>
    );
  };

  return (
    <Box className={classes.root}>
      <MiBox
        headerTitle="Add Areas, Items and Floors"
        headerAction={
          <Box className={classes.headerActionBox}>
            {(planAreas?.length > 0 || addedFloorIds.length > 0) && (
              <MiButton
                title="Add Floor"
                variant={BUTTON_VARIANTS.CONTAINED}
                onClick={handleAddFloor}
                size="small"
                startIcon={{ path: mdiPlus }}
                disabled={
                  addedFloors.length >= floors.length ||
                  openAreaDrawer ||
                  showCategoryFilter
                }
              />
            )}
          </Box>
        }
        className={classes.miBox}
        height="auto"
      >
        <Box className={classes.contentBox}>
          {addedFloors.map((floor) => {
            const floorAreas = groupedAreas[floor.id] || [];

            return (
              <Box
                key={floor.id}
                className={classes.floorBox}
                sx={{
                  border:
                    openAreaDrawer && selectedFloor?.id === floor.id
                      ? `2px solid ${theme.palette.primary.accent}`
                      : undefined,
                }}
              >
                <Box className={classes.floorHeaderBox}>
                  <Typography fontWeight={theme.fontWeight.bold}>
                    {floor.name}
                  </Typography>
                  <Box className={classes.floorHeaderActionBox}>
                    <MiButton
                      title="Add Area"
                      variant={BUTTON_VARIANTS.OUTLINED}
                      startIcon={{ path: mdiPlus, iconSize: 0.8 }}
                      size="small"
                      onClick={() => {
                        setSelectedFloor(floor);
                        setOpenAreaDrawer(true);
                      }}
                      disabled={showCategoryFilter}
                    />
                    <MiMenu
                      disabled={openAreaDrawer || showCategoryFilter}
                      data={[
                        {
                          label: "Remove Floor",
                          action: () => handleRemoveFloor(floor),
                        },
                      ]}
                    />
                  </Box>
                </Box>
                <Box className={classes.areaBox}>
                  {renderColumn(floorAreas, AREA_COLUMNS.LEFT)}
                  {renderColumn(floorAreas, AREA_COLUMNS.RIGHT)}
                </Box>
              </Box>
            );
          })}
        </Box>
        <MiPlaceholderArea
          open={planAreas?.length === 0 && addedFloorIds.length === 0}
          text="No Floors Added"
          helperText="Add your first floor above to begin setting up your bid"
          iconComponent={
            <MiIcon
              path={mdiFloorPlan}
              color={theme.palette.grey.main}
              size={2}
              className={classes.floorIcon}
            />
          }
          containerStyle={{
            backgroundColor: theme.palette.primary.light,
            margin: theme.spacing(1),
          }}
          buttons={[
            {
              title: "Add Floor",
              startIcon: mdiPlus,
              variant: BUTTON_VARIANTS.CONTAINED,
              onClick: handleAddFloor,
            },
          ]}
        />
      </MiBox>
      <AddAreaDrawer
        open={openAreaDrawer}
        onClose={() => {
          setOpenAreaDrawer(false);
          setPreviewAreas([]);
        }}
        selectedFloor={selectedFloor}
        setPreviewAreas={setPreviewAreas}
      />
      <SearchResultsContainer
        open={openMaterialResults}
        flex={0.6}
        onClose={() => {
          setSelectedItem(null);
          resetMaterialDrawerStates();
          setOpenMaterialResults(false);
        }}
        alertTitle="Select Item(s) and add to Area(s)."
        alertDescription="After selecting the item, choose the Area(s) and select the Add to Area(s) button."
        storageKey="bidMaterials-alert"
        results={materialResults}
        hasFilters={!!search || !!selectedCategory?.categoryId}
        handleResetFilterSelection={resetMaterialDrawerStates}
        renderResults={() =>
          materialResults.map((material: IMaterialsResponse, index: number) => (
            <MaterialResultCard
              key={material.id}
              index={index}
              material={material}
              isSelected={selectedItem?.id === material.id}
              handleSelectItem={handleSelectItem}
              isSuppliedByBuilder={isSuppliedByBuilder}
              setIsSuppliedByBuilder={setIsSuppliedByBuilder}
              checkedAreas={checkedAreas}
              planAreas={planAreas}
              handleCheckArea={handleCheckArea}
              handleCancelItem={handleCancelItem}
              handleAddItemToAreas={handleAddItemToAreas}
            />
          ))
        }
      />
      <CategoryFilterDrawer
        open={showCategoryFilter}
        onClose={handleCloseCategoryFilter as any}
        title="Filter Items"
        categoryFilterOptions={categoryFilterOptions}
        search={search}
        selectedCategory={selectedCategory}
        selectedAttributeValues={selectedAttributeValues}
        setSearch={setSearch}
        handleCategoryClick={handleCategoryClick}
        handleAttributeValueClick={handleAttributeValueClick}
        handleReset={resetMaterialDrawerStates}
        handleResetSelection={resetMaterialDrawerStates}
      />
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    overflow: "auto",
    height: "100%",
  },
  headerActionBox: {
    display: "flex",
    gap: theme.spacing(1),
  },
  miBox: {
    width: "100%",
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  contentBox: {
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
    flex: 1,
    gap: theme.spacing(2),
  },
  floorBox: {
    borderRadius: 4,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  floorHeaderBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    backgroundColor: theme.palette.primary.light,
    padding: theme.spacing(1, 2),
    borderRadius: 4,
  },
  floorHeaderActionBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  areaBox: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  columnBox: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minWidth: 400,
    gap: theme.spacing(2),
    "@container (min-width: 1200px)": {
      flexWrap: "nowrap",
    },
    "@container (max-width: 1199px)": {
      flexWrap: "wrap",
    },
  },
  floorIcon: {
    borderRadius: "50%",
    backgroundColor: theme.palette.common.white,
    padding: 8,
  },
}));
